import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useOnMount, usePersistentCallback, useThrottle } from '@prophecy/utils/react/hooks';
import { BoldPlugin, ItalicPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { BlockquotePlugin } from '@udecode/plate-block-quote/react';
import { SoftBreakPlugin } from '@udecode/plate-break/react';
import { CodeBlockPlugin } from '@udecode/plate-code-block/react';
import { TableCellHeaderPlugin, TableCellPlugin } from '@udecode/plate-table/react';
import { Plate, usePlateEditor } from '@udecode/plate/react';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useTransition } from 'react';
import { MentionProvider } from '../Mention/MentionContext';
import { UI_TYPOGRAPHY_ROOT_CLASS } from '../theme';
import { withMarkdownContext } from './context';
import { baseComponentOverrides, baseMarkdownPlugins } from './editorConfig';
import { FixedToolbarPlugin } from './FixedToolbarPlugin';
import { FloatingToolbarPlugin } from './FloatingToolbarPlugin';
import { deserializeMd } from './markdownDeserialize';
import { serializeToMd } from './markdownSerialize';
import { StyledMarkdownContainer, StyledMarkdownEditor, StyledPlateContent, StyledEditorBody, StyledLeftPanel } from './styled';
import { handleFormatKeyBinding } from './utils';
// scrollTopGlobalValue is maintained to make sure the scroll position is retained even when the editor is remounted.
// This was done due to bug in the editor where changing the comments array would led to editor not rendering the content properly.
// We could remove this once the comments updation doesn't lead to the editor not rendering the content properly.
let scrollTopGlobalValue = 0;
const useMarkdownScrollHeight = ()=>{
    const scrollTopRef = useRef(scrollTopGlobalValue);
    const setScrollTop = useCallback((scrollTop)=>{
        scrollTopRef.current = scrollTop;
        scrollTopGlobalValue = scrollTop;
    }, []);
    return {
        setScrollTop
    };
};
const MarkdownEditorComponent = ({ className, value, onChange, placeholder, readOnly, showFixedToolbar = true, showFloatingToolbar = true, scrollToBottomOnValueChange = false, getMentionSuggestions, getMentionValue, renderMentionSuggestion, renderMention, onMentionTrigger, platePlugins, onScrollPositionChange, initialScrollPosition = 0, plateValue, renderLeftPanel })=>{
    const { setScrollTop } = useMarkdownScrollHeight();
    const containerRef = useRef(null);
    const editorContentRef = useRef(null);
    const internalValueRef = useRef(value);
    const plateEditorOptions = useMemo(()=>{
        return {
            override: {
                components: {
                    ...baseComponentOverrides
                }
            },
            plugins: [
                showFixedToolbar && FixedToolbarPlugin,
                showFloatingToolbar && FloatingToolbarPlugin,
                BoldPlugin.configure({
                    handlers: {
                        onKeyDownCapture: ({ editor, event })=>{
                            handleFormatKeyBinding({
                                editor: editor,
                                event,
                                key: 'b'
                            });
                        }
                    }
                }),
                ItalicPlugin.configure({
                    handlers: {
                        onKeyDownCapture: ({ editor, event })=>{
                            handleFormatKeyBinding({
                                editor: editor,
                                event,
                                key: 'i'
                            });
                        }
                    }
                }),
                UnderlinePlugin.configure({
                    handlers: {
                        onKeyDownCapture: ({ editor, event })=>{
                            handleFormatKeyBinding({
                                editor: editor,
                                event,
                                key: 'u'
                            });
                        }
                    }
                }),
                SoftBreakPlugin.configure({
                    options: {
                        rules: [
                            {
                                hotkey: 'shift+enter'
                            },
                            {
                                hotkey: 'enter',
                                query: {
                                    allow: [
                                        CodeBlockPlugin.key,
                                        BlockquotePlugin.key,
                                        TableCellPlugin.key,
                                        TableCellHeaderPlugin.key
                                    ]
                                }
                            }
                        ]
                    }
                }),
                ...baseMarkdownPlugins,
                ...platePlugins || []
            ].filter(Boolean)
        };
    }, [
        platePlugins,
        showFixedToolbar,
        showFloatingToolbar
    ]);
    const editor = usePlateEditor({
        value: (editor)=>{
            if (plateValue) {
                return plateValue;
            }
            const internalValue = deserializeMd(editor, value || '');
            return internalValue;
        },
        override: plateEditorOptions.override,
        plugins: plateEditorOptions.plugins
    });
    // defer prop value update, so if multiple updates are coming its batched and reflected
    const deferredValue = useDeferredValue(value);
    const [, startTransition] = useTransition();
    const updateEditorValue = usePersistentCallback((editorValue)=>{
        startTransition(()=>{
            // update the editor state in non blocking way
            editor.tf.reset();
            editor.children = editorValue;
        });
    });
    useEffect(()=>{
        if (plateValue) {
            return;
        }
        if (internalValueRef.current !== deferredValue) {
            const deserializedValue = deserializeMd(editor, deferredValue || '');
            updateEditorValue(deserializedValue);
        }
    }, [
        deferredValue,
        editor,
        updateEditorValue,
        plateValue
    ]);
    useEffect(()=>{
        const ref = editorContentRef.current;
        if (!ref) return;
        let lastStableScrollTop = ref.scrollTop;
        const handleScroll = ()=>{
            const newScrollTop = ref.scrollTop;
            // Restore abrupt mutation-induced jumps from high scrollTop to 0.
            if (lastStableScrollTop > 100 && newScrollTop === 0) {
                const restoreTo = lastStableScrollTop;
                requestAnimationFrame(()=>{
                    ref.scrollTo({
                        top: restoreTo,
                        behavior: 'instant'
                    });
                });
                return;
            }
            lastStableScrollTop = newScrollTop;
            setScrollTop(newScrollTop);
            onScrollPositionChange?.(newScrollTop);
        };
        ref.addEventListener('scroll', handleScroll);
        return ()=>{
            ref.removeEventListener('scroll', handleScroll);
        };
    }, [
        editorContentRef,
        setScrollTop,
        onScrollPositionChange
    ]);
    const throttledScrollToBottom = useThrottle(()=>{
        if (!editorContentRef.current) return;
        const scrollHeight = editorContentRef.current.scrollHeight;
        if (scrollHeight) {
            editorContentRef.current.scrollTo({
                top: scrollHeight,
                behavior: 'smooth'
            });
        }
    }, 500);
    useEffect(()=>{
        // When markdown is streaming, scroll to bottom to show the new content
        if (scrollToBottomOnValueChange) throttledScrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        value,
        scrollToBottomOnValueChange
    ]);
    useOnMount(()=>{
        const ref = editorContentRef.current;
        if (!ref) return;
        // Use initialScrollPosition if provided
        // Avoid using scrollTopGlobalValue to prevent using same scroll
        // position for multiple instances of the editor
        const scrollPosition = initialScrollPosition || 0;
        if (scrollPosition > 0) {
            requestAnimationFrame(()=>{
                ref.scrollTo({
                    top: scrollPosition,
                    behavior: 'instant'
                });
            });
        }
    });
    const handleChange = ({ value })=>{
        // Use markdown serialization to maintain markdown formatting
        const serializedValue = serializeToMd(value);
        internalValueRef.current = serializedValue;
        if (!readOnly) {
            onChange?.(serializedValue);
        }
    };
    const renderEditable = useCallback((editable)=>/*#__PURE__*/ _jsxs(StyledEditorBody, {
            children: [
                renderLeftPanel && /*#__PURE__*/ _jsx(StyledLeftPanel, {
                    children: renderLeftPanel
                }),
                editable
            ]
        }), [
        renderLeftPanel
    ]);
    return /*#__PURE__*/ _jsx(MentionProvider, {
        value: {
            getMentionSuggestions,
            getMentionValue: getMentionValue,
            renderMentionSuggestion: renderMentionSuggestion,
            renderMention,
            onMentionTrigger,
            editorContainerRef: editorContentRef
        },
        children: /*#__PURE__*/ _jsx(StyledMarkdownContainer, {
            ref: containerRef,
            children: /*#__PURE__*/ _jsx(StyledMarkdownEditor, {
                className: className,
                children: /*#__PURE__*/ _jsx(Plate, {
                    editor: editor,
                    onValueChange: handleChange,
                    readOnly: readOnly,
                    children: /*#__PURE__*/ _jsx(StyledPlateContent, {
                        ref: editorContentRef,
                        placeholder: placeholder,
                        className: UI_TYPOGRAPHY_ROOT_CLASS,
                        disabled: readOnly,
                        renderEditable: renderEditable
                    })
                })
            })
        })
    });
};
export const MarkdownEditor = withMarkdownContext(MarkdownEditorComponent);
